/* eslint-disable */
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import MemberBadge from "../components/MemberBadge";

/**
 * Session 57 · Admin → Member timeline drilldown.
 *
 * Merges declarative milestones (from the user document timestamps)
 * with the event log stored in `member_timeline`, sorted chronologically.
 */
const MILESTONE_LABEL = {
  invitation_sent: "Invitation sent",
  invitation_opened: "Invitation opened",
  first_login: "First login",
  password_created: "Password created",
  profile_completed: "Profile completed",
  first_workspace_viewed: "First workspace viewed",
  first_booking: "First booking",
  first_hours_visit: "First Métier Hours visit",
  first_commons_post: "First Commons post",
  feedback_submitted: "Feedback submitted",
};

export default function AdminMemberTimeline() {
  const { userId } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/admin/members/${userId}/timeline`);
        setData(r.data);
      } finally { setLoading(false); }
    })();
  }, [userId]);

  if (!user || user.role !== "admin") return <div style={{ padding: 40 }}>Admin only.</div>;
  if (loading) return <div style={{ padding: 40 }}>Loading…</div>;
  if (!data) return <div style={{ padding: 40 }}>Member not found.</div>;

  const merged = [
    ...(data.milestones || [])
      .filter((m) => m.at)
      .map((m) => ({ at: m.at, kind: "milestone", event: m.event, meta: {} })),
    ...(data.events || []).map((e) => ({ at: e.at, kind: "event", event: e.event, meta: e.meta || {} })),
  ].sort((a, b) => (a.at || "").localeCompare(b.at || ""));

  return (
    <div data-testid="admin-member-timeline" style={{ minHeight: "100vh", background: "#FAF8F3", padding: "40px 24px 80px", fontFamily: "Helvetica,Arial,sans-serif", color: "#1B2340" }}>
      <div style={{ maxWidth: 780, margin: "0 auto" }}>
        <Link to="/admin/members" style={{ color: "#B8913A", fontSize: 13, fontWeight: 600, textDecoration: "none" }}>← All members</Link>

        <div style={{ marginTop: 12, background: "#fff", borderRadius: 12, border: "1px solid rgba(27,35,64,0.08)", padding: "24px 28px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div>
              <h1 style={{ fontFamily: "Georgia,serif", fontSize: 26, margin: 0, fontWeight: 500 }}>{data.user.name || "—"}</h1>
              <p style={{ fontSize: 13, color: "rgba(27,35,64,0.6)", margin: "4px 0 0" }}>{data.user.email}</p>
            </div>
            <MemberBadge member_type={data.user.member_type} founding_member={data.user.founding_member} size="md" />
          </div>
        </div>

        <div style={{ marginTop: 22, background: "#fff", borderRadius: 12, border: "1px solid rgba(27,35,64,0.08)", padding: "20px 24px" }}>
          <div style={{ fontSize: 12, letterSpacing: "0.14em", textTransform: "uppercase", color: "#B8913A", fontWeight: 700, marginBottom: 14 }}>Activity Timeline</div>
          {merged.length === 0 ? (
            <p style={{ fontSize: 13, color: "rgba(27,35,64,0.55)" }}>No activity yet.</p>
          ) : (
            <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
              {merged.map((row, i) => (
                <li key={i} data-testid={`timeline-event-${i}`} style={{ display: "flex", gap: 14, padding: "10px 0", borderBottom: "1px dashed rgba(27,35,64,0.08)" }}>
                  <div style={{
                    width: 8, height: 8, borderRadius: "50%",
                    background: row.kind === "milestone" ? "#B8913A" : "rgba(27,35,64,0.4)",
                    marginTop: 8, flexShrink: 0,
                  }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 14, fontWeight: 600 }}>{MILESTONE_LABEL[row.event] || row.event}</div>
                    <div style={{ fontSize: 12, color: "rgba(27,35,64,0.55)" }}>{new Date(row.at).toLocaleString()}</div>
                    {row.meta && Object.keys(row.meta).length > 0 && (
                      <pre style={{ fontSize: 11, color: "rgba(27,35,64,0.6)", background: "#FAF8F3", padding: "6px 10px", borderRadius: 6, marginTop: 6, whiteSpace: "pre-wrap" }}>
                        {JSON.stringify(row.meta, null, 2)}
                      </pre>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
